import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { SecondaryButton } from "@/components/brand/SecondaryButton";
import { BackLink } from "@/components/brand/BackLink";
import { CalculatorDisclaimer } from "@/components/brand/CalculatorDisclaimer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  fmtUSD,
  emailShareLink,
  logCalculatorCompletion,
} from "@/lib/calculators";

export const Route = createFileRoute("/tools/estate")({
  head: () => ({
    meta: [
      { title: "Estate Transfer Calculator — What Actually Reaches Your Heirs | Currency Thoughts" },
      {
        name: "description",
        content:
          "Estate tax, probate, and unprotected accounts can quietly cut a legacy in half. See what actually transfers — and what doesn't.",
      },
      { property: "og:title", content: "Estate Transfer — What actually reaches your heirs" },
      {
        property: "og:description",
        content:
          "Federal estate tax, state estate tax, probate friction. The number on paper is rarely the number your family receives.",
      },
    ],
    links: [{ rel: "canonical", href: "/tools/estate" }],
  }),
  component: EstatePage,
});

const FED_EXEMPTION_2026 = 7_000_000;
const FED_RATE = 0.4;

interface EState {
  realEstate: number;
  investments: number;
  retirement: number;
  business: number;
  lifeInsurance: number;
  insuranceInTrust: boolean;
  filingStatus: "single" | "married";
  stateEstateRate: number; // %
  probatePercent: number; // % of probate-subject assets
}

function calcEstate(e: EState) {
  const exemption = e.filingStatus === "married" ? FED_EXEMPTION_2026 * 2 : FED_EXEMPTION_2026;
  // Gross estate
  const insuranceInGross = e.insuranceInTrust ? 0 : e.lifeInsurance;
  const grossEstate =
    e.realEstate + e.investments + e.retirement + e.business + insuranceInGross;

  const taxable = Math.max(0, grossEstate - exemption);
  const federalTax = taxable * FED_RATE;
  const stateTax = grossEstate * (e.stateEstateRate / 100);

  // Probate friction approximated on real estate + investments + business (not retirement w/ beneficiary, not life ins.)
  const probateBase = e.realEstate + e.investments + e.business;
  const probateCost = probateBase * (e.probatePercent / 100);

  const totalLost = federalTax + stateTax + probateCost;
  // Life insurance proceeds reach heirs regardless (assumes beneficiary named).
  const reachesHeirs = grossEstate + (e.insuranceInTrust ? e.lifeInsurance : 0) - totalLost;
  const lossPct = grossEstate > 0 ? (totalLost / grossEstate) * 100 : 0;

  return { exemption, grossEstate, taxable, federalTax, stateTax, probateCost, totalLost, reachesHeirs, lossPct };
}

function EstatePage() {
  const [done, setDone] = useState(false);
  const [e, setE] = useState<EState>({
    realEstate: 800_000,
    investments: 600_000,
    retirement: 500_000,
    business: 0,
    lifeInsurance: 1_000_000,
    insuranceInTrust: false,
    filingStatus: "married",
    stateEstateRate: 0,
    probatePercent: 4,
  });
  const out = useMemo(() => calcEstate(e), [e]);

  async function onComplete() {
    const r = await logCalculatorCompletion("estate", e, out);
    if (r.saved) toast.success("Saved to your Vault");
    setDone(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  return (
    <div className="min-h-screen bg-cream-50">
      <Navbar />
      <main className="mx-auto max-w-6xl px-6 pt-12 pb-24">
        <BackLink to="/" label="Back to home" />

        {!done ? (
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-12">
            <div>
              <Eyebrow>— Estate Transfer —</Eyebrow>
              <h1 className="mt-5 font-display text-[36px] md:text-[42px] leading-[1.08] text-navy-900">
                What actually reaches your heirs?
              </h1>
              <p className="mt-5 font-sans text-[16px] leading-[1.65] text-charcoal-900 max-w-[58ch]">
                Most families work a lifetime to build a number — then lose 20
                to 40 percent of it in the transfer. Estate tax, state tax, and
                probate are the quiet thieves. See your real after-transfer
                number now, while there&rsquo;s time to plan.
              </p>

              <div className="mt-8 bg-white border border-bronze-600/25 rounded-[4px] p-7 shadow-soft space-y-7">
                <Section title="Assets">
                  <Money label="Real estate" value={e.realEstate} onChange={(v) => setE({ ...e, realEstate: v })} />
                  <Money label="Investments (taxable)" value={e.investments} onChange={(v) => setE({ ...e, investments: v })} />
                  <Money label="Retirement accounts" value={e.retirement} onChange={(v) => setE({ ...e, retirement: v })} />
                  <Money label="Business interests" value={e.business} onChange={(v) => setE({ ...e, business: v })} />
                  <Money label="Life insurance death benefit" value={e.lifeInsurance} onChange={(v) => setE({ ...e, lifeInsurance: v })} />
                  <div>
                    <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">Insurance ownership</Label>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <ToggleChip on={!e.insuranceInTrust} label="Personally owned" onClick={() => setE({ ...e, insuranceInTrust: false })} />
                      <ToggleChip on={e.insuranceInTrust} label="Owned by ILIT" onClick={() => setE({ ...e, insuranceInTrust: true })} />
                    </div>
                  </div>
                </Section>

                <Section title="Context">
                  <div>
                    <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">Filing status</Label>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <ToggleChip on={e.filingStatus === "single"} label="Single" onClick={() => setE({ ...e, filingStatus: "single" })} />
                      <ToggleChip on={e.filingStatus === "married"} label="Married" onClick={() => setE({ ...e, filingStatus: "married" })} />
                    </div>
                  </div>
                  <Slider label="State estate tax rate" value={e.stateEstateRate} min={0} max={16} step={1} suffix="%" onChange={(v) => setE({ ...e, stateEstateRate: v })} />
                  <Slider label="Probate friction" value={e.probatePercent} min={0} max={10} step={1} suffix="%" onChange={(v) => setE({ ...e, probatePercent: v })} />
                </Section>

                <div className="flex justify-end">
                  <PrimaryButton onClick={onComplete} size="lg">See what transfers →</PrimaryButton>
                </div>
              </div>
            </div>

            {/* Live transfer visualizer */}
            <aside className="hidden lg:block">
              <div className="sticky top-24 bg-navy-900 text-cream-50 rounded-[4px] p-6 shadow-soft">
                <p className="font-sans text-[10px] uppercase tracking-[0.22em] text-bronze-400">
                  Reaches your heirs
                </p>
                <p
                  className="mt-3 font-display text-[36px] leading-none"
                  style={{
                    backgroundImage: "linear-gradient(180deg, var(--color-bronze-400), var(--color-bronze-600))",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                  }}
                >
                  {fmtUSD(out.reachesHeirs)}
                </p>

                <TransferBar reaches={out.reachesHeirs} lost={out.totalLost} />

                <div className="mt-6 space-y-3 font-sans text-[12px]">
                  <LineRow label="Gross estate" value={fmtUSD(out.grossEstate)} />
                  <LineRow label="Federal estate tax" value={"− " + fmtUSD(out.federalTax)} />
                  <LineRow label="State estate tax" value={"− " + fmtUSD(out.stateTax)} />
                  <LineRow label="Probate friction" value={"− " + fmtUSD(out.probateCost)} />
                </div>
              </div>
            </aside>
          </div>
        ) : (
          <Result e={e} out={out} onRestart={() => setDone(false)} />
        )}
      </main>
      <Footer />
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="font-sans text-[10px] uppercase tracking-[0.22em] text-bronze-600">{title}</p>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-5">{children}</div>
    </div>
  );
}

function Money({ label, value, onChange }: { label: string; value: number; onChange: (n: number) => void }) {
  return (
    <div>
      <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">{label}</Label>
      <div className="mt-2 relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 font-display text-navy-800">$</span>
        <Input
          type="number"
          min={0}
          value={value}
          onChange={(ev) => onChange(Math.max(0, Number(ev.target.value) || 0))}
          className="pl-7 font-display text-[16px] text-navy-900 h-11"
        />
      </div>
    </div>
  );
}

function Slider({
  label, value, min, max, step = 1, suffix = "", onChange,
}: { label: string; value: number; min: number; max: number; step?: number; suffix?: string; onChange: (n: number) => void }) {
  return (
    <div>
      <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">{label}</Label>
      <div className="mt-2 flex items-center gap-3">
        <input type="range" min={min} max={max} step={step} value={value} onChange={(ev) => onChange(Number(ev.target.value))} className="w-full accent-bronze-600" />
        <span className="font-display text-navy-900 text-[16px] min-w-[4ch] text-right">{value}{suffix}</span>
      </div>
    </div>
  );
}

function ToggleChip({ on, label, onClick }: { on: boolean; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "p-3 rounded-[2px] border text-[12px] font-sans transition-colors text-left " +
        (on
          ? "border-bronze-600 bg-bronze-600/10 text-navy-900"
          : "border-bronze-600/25 bg-white text-charcoal-500 hover:border-bronze-600/60")
      }
    >
      {label}
    </button>
  );
}

function TransferBar({ reaches, lost }: { reaches: number; lost: number }) {
  const total = Math.max(1, reaches + lost);
  const reachPct = (reaches / total) * 100;
  return (
    <div className="mt-5">
      <div className="h-3 rounded-full overflow-hidden bg-navy-800/60 flex">
        <div
          className="h-full transition-all duration-500"
          style={{
            width: `${reachPct}%`,
            background: "linear-gradient(90deg, var(--color-bronze-400), var(--color-bronze-600))",
          }}
        />
        <div
          className="h-full transition-all duration-500"
          style={{
            width: `${100 - reachPct}%`,
            background: "rgba(180, 58, 58, 0.7)",
          }}
        />
      </div>
      <div className="mt-2 flex items-center justify-between font-sans text-[11px] text-cream-50/80">
        <span>Heirs · {reachPct.toFixed(0)}%</span>
        <span>Lost · {(100 - reachPct).toFixed(0)}%</span>
      </div>
    </div>
  );
}

function LineRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between border-b border-bronze-400/20 pb-2">
      <span className="text-cream-50/80">{label}</span>
      <span className="text-bronze-400 font-semibold">{value}</span>
    </div>
  );
}

function Result({
  e, out, onRestart,
}: { e: EState; out: ReturnType<typeof calcEstate>; onRestart: () => void }) {
  const share = emailShareLink(
    "Our estate transfer estimate",
    `Currency Thoughts estate projection:\n\n  Gross estate: ${fmtUSD(out.grossEstate)}\n  Federal estate tax: ${fmtUSD(out.federalTax)}\n  State estate tax: ${fmtUSD(out.stateTax)}\n  Probate friction: ${fmtUSD(out.probateCost)}\n  Reaches heirs: ${fmtUSD(out.reachesHeirs)}\n\nSee the model: https://www.currencythoughts.net/tools/estate`,
  );
  return (
    <div className="mt-8">
      <div className="mx-auto max-w-3xl text-center">
        <Eyebrow align="center">— What Actually Reaches Your Heirs —</Eyebrow>
        <h1
          className="mt-6 font-display leading-[1] text-[64px] md:text-[96px]"
          style={{
            backgroundImage: "linear-gradient(180deg, var(--color-bronze-400), var(--color-bronze-600))",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            color: "transparent",
          }}
        >
          {fmtUSD(out.reachesHeirs)}
        </h1>
        <p className="mt-6 font-sans text-[15px] md:text-[16px] leading-[1.7] text-charcoal-900 max-w-[60ch] mx-auto">
          You built an estate worth {fmtUSD(out.grossEstate)}. {fmtUSD(out.totalLost)} —
          about {out.lossPct.toFixed(0)}% — never reaches your family unless
          this is planned. The good news: each line below has a quiet,
          well-established strategy to reduce it.
        </p>
      </div>

      <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-5xl mx-auto">
        <Stat label="Federal tax" value={fmtUSD(out.federalTax)} sub={`Exempt ${fmtUSD(out.exemption)}`} />
        <Stat label="State tax" value={fmtUSD(out.stateTax)} />
        <Stat label="Probate friction" value={fmtUSD(out.probateCost)} />
        <Stat label="Total lost" value={fmtUSD(out.totalLost)} highlight />
      </div>

      <div className="mt-10 max-w-4xl mx-auto bg-navy-900 text-cream-50 rounded-[4px] p-7 shadow-soft">
        <p className="font-sans text-[10px] uppercase tracking-[0.22em] text-bronze-400">Three quiet strategies</p>
        <ul className="mt-4 font-sans text-[14px] leading-relaxed text-cream-50/90 space-y-2 max-w-[60ch]">
          <li>· An <strong>ILIT</strong> can move life insurance out of your taxable estate.</li>
          <li>· A <strong>revocable trust</strong> removes most assets from probate entirely.</li>
          <li>· <strong>Named beneficiaries</strong> on retirement and insurance bypass probate by default.</li>
        </ul>
      </div>

      <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
        <PrimaryButton asChild size="lg">
          <a href="/book?source=estate">Plan with a Strategist →</a>
        </PrimaryButton>
        <SecondaryButton asChild size="lg">
          <Link to="/tools/family-snapshot">Take the Family Snapshot →</Link>
        </SecondaryButton>
      </div>

      <div className="mt-10 max-w-3xl mx-auto text-center">
        <p className="font-sans text-[13px] text-charcoal-500">
          Start with the basics —{" "}
          <Link
            to="/academy/lessons/$lessonSlug"
            params={{ lessonSlug: "estate-tax-myths" }}
            className="text-bronze-600 hover:text-bronze-400 underline underline-offset-4"
          >
            Estate tax myths that cost real families money →
          </Link>
        </p>
        <div className="mt-5 flex items-center justify-center gap-3 text-[12px]">
          <a href={share} className="font-sans text-bronze-600 hover:text-bronze-400 underline underline-offset-4">Email this to my spouse</a>
          <span className="text-charcoal-500">·</span>
          <button onClick={onRestart} className="font-sans text-charcoal-500 hover:text-navy-800 underline underline-offset-4">Recalculate</button>
        </div>
      </div>

      <div className="mt-12 max-w-3xl mx-auto">
        <CalculatorDisclaimer />
      </div>
    </div>
  );
}

function Stat({ label, value, sub, highlight }: { label: string; value: string; sub?: string; highlight?: boolean }) {
  return (
    <div
      className={
        "rounded-[4px] p-5 text-center shadow-soft border " +
        (highlight ? "bg-navy-900 border-bronze-600/40 text-cream-50" : "bg-white border-bronze-600/20")
      }
    >
      <p className={"font-sans text-[10px] uppercase tracking-[0.22em] " + (highlight ? "text-bronze-400" : "text-charcoal-500")}>{label}</p>
      <p className={"mt-3 font-display text-[22px] " + (highlight ? "text-bronze-400" : "text-navy-900")}>{value}</p>
      {sub && <p className={"mt-1 font-sans text-[10px] " + (highlight ? "text-cream-50/60" : "text-charcoal-500")}>{sub}</p>}
    </div>
  );
}